import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { ToastHostComponent } from './core/components/toast/toast-host.component';
import { PwaInstallBannerComponent } from './core/pwa/pwa-install-banner.component';
import { Store } from '@ngrx/store';
import { Auth, authState, User } from '@angular/fire/auth';
import { AppState } from './core/store/app.state';
import { AuthService } from './core/services/auth.service';
import { InviteFlowService } from './core/services/invite-flow.service';
import { I18nService } from './core/services/i18n.service';
import * as AuthActions from './core/store/auth/auth.actions';
import { selectIsAuthenticated, selectUser } from './core/store/auth/auth.selectors';
import { take } from 'rxjs/operators';

@Component({
  imports: [RouterModule, ToastHostComponent, PwaInstallBannerComponent],
  selector: 'app-root',
  templateUrl: './app.html',
})
export class App implements OnInit {
  protected title = 'frontend';
  private store = inject(Store<AppState>);
  private auth = inject(Auth);
  private authService = inject(AuthService);
  private inviteFlowService = inject(InviteFlowService);
  private i18nService = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    // authState() deve essere chiamato nell'injection context (costruttore o field init),
    // altrimenti AngularFire emette il warning "Calling Firebase APIs outside of an Injection context"
    authState(this.auth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((firebaseUser) => this.onAuthStateChange(firebaseUser as User | null));
  }

  ngOnInit() {
    this.i18nService.init();
  }

  private onAuthStateChange(firebaseUser: User | null): void {
    if (this.inviteFlowService.isInProgress()) return;

    if (firebaseUser) {
      firebaseUser
        .getIdToken()
        .then((token) => {
          localStorage.setItem('firebaseToken', token);
          this.store
            .select(selectIsAuthenticated)
            .pipe(take(1))
            .subscribe((isAuthenticated) => {
              if (!isAuthenticated) {
                this.store.dispatch(AuthActions.loadUser());
              } else {
                this.store
                  .select(selectUser)
                  .pipe(take(1))
                  .subscribe((user) => {
                    if (user && user.firebaseUid !== firebaseUser.uid) {
                      this.store.dispatch(AuthActions.loadUser());
                    }
                  });
              }
            });
        })
        .catch((error) => {
          console.error('Error getting Firebase token:', error);
          const token = this.authService.getToken();
          if (token) {
            this.store.dispatch(AuthActions.loadUser());
          }
        });
    } else {
      // Nessun utente Firebase: non fare loadUser() (evita 401 dopo logout per race token/localStorage).
      // Se lo store dice ancora "autenticato" (es. logout non ancora processato), allinea con logout.
      this.store
        .select(selectIsAuthenticated)
        .pipe(take(1))
        .subscribe((isAuthenticated) => {
          if (isAuthenticated) {
            this.store.dispatch(AuthActions.logout());
          }
        });
    }
  }
}
